import { CartItemInput, UpdateCartItem } from 'contexts/type/cart-type';
import { buildFingerPrint } from 'contexts/cart/utils/build-fingerprint';
import { isValueEmpty } from 'contexts/cart/utils/index';

interface UpdateCartArgs {
  prev: CartItemInput[];
  updates: UpdateCartItem[];
}

interface MergeCartItems {
  prevItem: CartItemInput;
  updateItem: CartItemInput;
}

// removes empty values from the result (empty means null, undefined or empty string, 0 - is not empty value here)
const stripEmpty = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => !isValueEmpty(v))) as T;

/**
 * Merges cart item data with the updates
 */
export const mergeCartItems = ({ prevItem, updateItem }: MergeCartItems): CartItemInput => {
  const merged = {
    ...prevItem,
    ...updateItem,
    quantity: prevItem.quantity + updateItem.quantity,
  };

  return stripEmpty(merged);
};

/**
 * Applies a list of updates to the current cart items and returns the next cart state.
 *
 * The function matches items by their fingerprint (see `buildFingerPrint`):
 * - For each item in `prev`, if there is a matching update in `updates`, it merges the fields (`{...prevItem, ...updateData}`).
 * - The updated item is kept only if `quantity >= 0`; otherwise it is removed.
 * - Updates without a valid fingerprint are ignored.
 *
 * Note: If `prev` is empty, the function returns an empty array (there is nothing to update).
 *
 * @param prev Current cart items.
 * @param updates List of item updates (each contains `updateData` used for merging).
 * @returns New cart items array after applying updates.
 */
export const updateCart = ({ prev, updates }: UpdateCartArgs): CartItemInput[] => {
  // if cart is empty there is nothing to update
  if (prev.length === 0) return [];

  const updatesMap = new Map<string, CartItemInput>();
  updates.forEach((update) => updatesMap.set(buildFingerPrint(update.target), update.updateData));
  const resultCartItems = new Map<string, CartItemInput>();

  prev.forEach((prevItem) => {
    const prevItemFingerPrint = buildFingerPrint(prevItem);
    // find element based on update target fingerprint
    const updateForItem = updatesMap.get(prevItemFingerPrint);

    if (updateForItem !== undefined) {
      const updatedItem = {
        ...prevItem,
        ...updateForItem,
      };
      // remove item from updates map after update
      updatesMap.delete(prevItemFingerPrint);

      // save by new fingerprint
      resultCartItems.set(buildFingerPrint(updatedItem), updatedItem);
      return;
    }

    const duplicateItem = resultCartItems.get(prevItemFingerPrint);

    if (duplicateItem !== undefined) {
      const updatedItem = {
        ...prevItem,
        ...duplicateItem,
        quantity: duplicateItem.quantity + prevItem.quantity,
      };
      resultCartItems.set(prevItemFingerPrint, updatedItem);
      return;
    }

    resultCartItems.set(prevItemFingerPrint, prevItem);
  });

  return Array.from(resultCartItems.values()).filter((item) => item.quantity > 0);
};
